import { useState } from 'react';
import { Modal, ModalBody } from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { VideoDirectory } from './components/video-directory';
import { VideoViewer } from './components/video-viewer';
import { useVideoData } from './hooks/use-video-data';
import { VideoLibraryTrackContext, type VideoLibraryTrack } from './use-video-library-track';
import * as styles from './video-library.css';

export type VideoLibraryModalData = {
  /** Video to open directly in the viewer; `null` shows the directory. */
  initialVideoId: string | null;
  /** Bumped on every `openVideoLibrary` call so a repeat open resets the viewer. */
  openRequestId: number;
  /** `track` bound below the workspace context — see `use-video-library-track.ts`. */
  track: VideoLibraryTrack;
};

export function VideoLibraryModal({ modalState, data, onClose }: PlumaManagedModalProps<VideoLibraryModalData>) {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(data.initialVideoId);
  // `openVideoLibrary` on an already-open library updates `data` in place —
  // reset the local selection so the modal switches to the requested video.
  const [prevOpenRequestId, setPrevOpenRequestId] = useState(data.openRequestId);
  if (data.openRequestId !== prevOpenRequestId) {
    setPrevOpenRequestId(data.openRequestId);
    setActiveVideoId(data.initialVideoId);
  }
  const { data: videos = [], isLoading, isError, refetch } = useVideoData();

  const handleSelectVideo = (videoId: string) => {
    setActiveVideoId(videoId);
  };

  const handleViewAll = () => {
    setActiveVideoId(null);
  };

  const handleRetry = () => {
    void refetch();
  };

  return (
    <Modal
      state={modalState}
      onClose={() => onClose()}
      title="Customer.io basics"
      className={styles.modal}
      data-test-video-library-modal
    >
      <ModalBody>
        <VideoLibraryTrackContext.Provider value={data.track}>
          {activeVideoId != null ? (
            <VideoViewer
              videoId={activeVideoId}
              videos={videos}
              onSelectVideo={handleSelectVideo}
              onViewAll={handleViewAll}
            />
          ) : (
            <VideoDirectory
              videos={videos}
              isLoading={isLoading}
              isError={isError}
              onSelectVideo={handleSelectVideo}
              onRetry={handleRetry}
            />
          )}
        </VideoLibraryTrackContext.Provider>
      </ModalBody>
    </Modal>
  );
}
